import type { Scene } from "../core/scene";

/**
 * Погодные эффекты: осадки на canvas, заливки на CSS.
 *
 * Почему пополам. Туман, дымка и солнечное свечение — это градиенты, и
 * градиентами им и следует быть: они почти не двигаются, а CSS кладёт их на
 * GPU бесплатно. Осадки же — дискретные объекты, и градиентом они не станут
 * никогда: сплошная штриховка с периодом 23px, которая раньше ехала целиком,
 * читалась как ползущие жалюзи, а второй слой поверх первого давал муар.
 *
 * Частица здесь не объект, а арифметика: положение — чистая функция номера и
 * времени, `y = frac(seed + t/cycle)`. Между кадрами состояния нет, поэтому
 * ни пауза, ни смена размера окна, ни возврат на вкладку не могут его
 * рассинхронизировать — а у модели с `drop.y += speed` именно это и ломается,
 * плюс на 120 Гц дождь льёт вдвое быстрее.
 */

/** Сколько частиц на полной плотности. Дальше растёт только видимая густота. */
const MAX_DROPS = 900;
/** Три плана по глубине: ближний крупнее и быстрее. Рисуются тремя вызовами. */
const BANDS = 3;
const FALL_PX = 1500;

/** Детерминированный шум вместо Math.random: у частицы i он всегда один. */
function noise(i: number, salt: number): number {
  let h = Math.imul(i ^ salt, 2654435761);
  h = (h ^ (h >>> 15)) >>> 0;
  return h / 4294967296;
}

const seeds = {
  x: new Float32Array(MAX_DROPS),
  y: new Float32Array(MAX_DROPS),
  depth: new Float32Array(MAX_DROPS),
  len: new Float32Array(MAX_DROPS),
  sway: new Float32Array(MAX_DROPS),
};
for (let i = 0; i < MAX_DROPS; i++) {
  seeds.x[i] = noise(i, 3);
  seeds.y[i] = noise(i, 5);
  seeds.depth[i] = 0.4 + noise(i, 7) * 0.6;
  seeds.len[i] = 0.5 + noise(i, 11) * 0.5;
  seeds.sway[i] = noise(i, 13) * Math.PI * 2;
}

const EMPTY: Scene = {
  precip: { kind: "none", density: 0, fall: 0 },
  wind: { angle: 0, strength: 0, shear: 0 },
  veil: { fog: 0, haze: 0 },
  light: { glow: 0, flash: 0 },
};

export class WeatherFx {
  private ctx: CanvasRenderingContext2D | null;
  private scene: Scene = EMPTY;
  private raf = 0;
  private t = 0;
  private last = 0;
  private w = 0;
  private h = 0;
  private calm = matchMedia("(prefers-reduced-motion: reduce)");

  constructor(private canvas: HTMLCanvasElement, private host: HTMLElement) {
    this.ctx = canvas.getContext("2d");
    addEventListener("resize", this.resize, { passive: true });
    document.addEventListener("visibilitychange", this.sync);
    this.calm.addEventListener("change", this.sync);
    this.resize();
  }

  /**
   * Новая погода. Заливки уезжают в CSS числами, а не классом состояния:
   * туман, дымка и свечение складываются, вместо того чтобы вытеснять
   * друг друга, — как и было задумано в сцене.
   */
  apply(scene: Scene) {
    this.scene = scene;
    const s = this.host.style;
    s.setProperty("--fog", scene.veil.fog.toFixed(3));
    s.setProperty("--haze", scene.veil.haze.toFixed(3));
    s.setProperty("--glow", scene.light.glow.toFixed(3));
    s.setProperty("--flash", scene.light.flash.toFixed(3));
    // Анимация крутится только у того слоя, который видно: пульсировать
    // прозрачным элементом — это пересчёт стиля каждый кадр ни за чем.
    this.host.classList.toggle("has-fog", scene.veil.fog > 0.02);
    this.host.classList.toggle("has-glow", scene.light.glow > 0.02);
    this.host.classList.toggle("has-flash", scene.light.flash > 0.05);
    this.sync();
  }

  destroy() {
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    removeEventListener("resize", this.resize);
    document.removeEventListener("visibilitychange", this.sync);
    this.calm.removeEventListener("change", this.sync);
  }

  private resize = () => {
    const dpr = Math.min(devicePixelRatio || 1, 2);
    this.w = innerWidth;
    this.h = innerHeight;
    this.canvas.width = Math.round(this.w * dpr);
    this.canvas.height = Math.round(this.h * dpr);
    this.ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (!this.raf) this.draw();
  };

  /** Крутим цикл только когда есть что двигать: музыка играет часами. */
  private sync = () => {
    const moving = this.scene.precip.density > 0 || this.scene.wind.strength > 0.25;
    const want = moving && !document.hidden && !this.calm.matches;
    if (want) {
      if (!this.raf) {
        this.last = performance.now();
        this.raf = requestAnimationFrame(this.frame);
      }
      return;
    }
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
    // Застывший кадр рисуем всегда, а не только когда было что останавливать:
    // при prefers-reduced-motion цикл не запускался ни разу, и дождь просто
    // не появлялся — вместо неподвижного дождя был пустой экран.
    this.draw();
  };

  private frame = (now: number) => {
    // Шаг по времени, а не по кадру: на 120 Гц скорость та же, что на 60.
    // Ограничение сверху — на возврат из фона, иначе частицы прыгнут.
    this.t += Math.min(0.05, (now - this.last) / 1000);
    this.last = now;
    this.draw();
    this.raf = requestAnimationFrame(this.frame);
  };

  private draw() {
    const ctx = this.ctx;
    if (!ctx) return;
    ctx.clearRect(0, 0, this.w, this.h);
    const { precip, wind } = this.scene;
    if (precip.kind !== "none" && precip.density > 0) {
      if (precip.kind === "snow") this.drawSnow(ctx);
      else this.drawRain(ctx);
    }
    if (wind.strength > 0.25 && precip.kind !== "rain") this.drawGusts(ctx);
  }

  /** Наклон с рывком от порывов: ровный ветер качки не даёт. */
  private tilt(): number {
    const { angle, shear } = this.scene.wind;
    return ((angle + Math.sin(this.t * 0.7) * shear * 9) * Math.PI) / 180;
  }

  private drawRain(ctx: CanvasRenderingContext2D) {
    const { density, fall } = this.scene.precip;
    const n = Math.round(MAX_DROPS * density);
    const slope = Math.tan(this.tilt());
    const span = this.h + 120;
    const spread = this.w + Math.abs(slope) * span + 200;

    // Направление один раз на кадр, а не дважды на каплю.
    const tilt = this.tilt();
    const dx = Math.sin(tilt);
    const dy = Math.cos(tilt);

    ctx.lineCap = "round";
    for (let b = 0; b < BANDS; b++) {
      const near = (b + 1) / BANDS;
      ctx.beginPath();
      ctx.lineWidth = 0.7 + near * 1.2;
      ctx.strokeStyle = `rgba(210,228,250,${(0.16 + near * 0.34) * (0.5 + density * 0.5)})`;
      for (let i = b; i < n; i += BANDS) {
        const depth = seeds.depth[i]!;
        const cycle = span / (FALL_PX * fall * depth);
        const y = ((seeds.y[i]! + this.t / cycle) % 1) * span - 60;
        const x = seeds.x[i]! * spread - 100 + y * slope;
        const len = (14 + seeds.len[i]! * 30) * depth;
        ctx.moveTo(x, y);
        ctx.lineTo(x + len * dx, y + len * dy);
      }
      ctx.stroke();
    }
  }

  private drawSnow(ctx: CanvasRenderingContext2D) {
    const { density, fall } = this.scene.precip;
    const n = Math.round(MAX_DROPS * 0.55 * density);
    const slope = Math.tan(this.tilt());
    const span = this.h + 60;
    const spread = this.w + Math.abs(slope) * span + 120;

    for (let b = 0; b < BANDS; b++) {
      const near = (b + 1) / BANDS;
      ctx.beginPath();
      ctx.fillStyle = `rgba(255,255,255,${(0.25 + near * 0.45) * (0.5 + density * 0.5)})`;
      for (let i = b; i < n; i += BANDS) {
        const depth = seeds.depth[i]!;
        const cycle = span / (FALL_PX * fall * depth);
        const y = ((seeds.y[i]! + this.t / cycle) % 1) * span - 30;
        // Снежинку ведёт вбок: без этого снег падает как дождь, только медленно.
        const sway = Math.sin(this.t * 0.6 + seeds.sway[i]!) * 20 * depth;
        const x = seeds.x[i]! * spread - 60 + y * slope + sway;
        ctx.moveTo(x, y);
        ctx.arc(x, y, 0.7 + near * 1.3, 0, Math.PI * 2);
      }
      ctx.fill();
    }
  }

  /** Ветер без осадков: редкие длинные штрихи, иначе его нечем показать. */
  private drawGusts(ctx: CanvasRenderingContext2D) {
    const { strength, shear } = this.scene.wind;
    const n = Math.round(6 + strength * 14);
    const dir = this.scene.wind.angle >= 0 ? 1 : -1;
    const speed = (220 + strength * 520) * dir;

    ctx.lineCap = "round";
    ctx.lineWidth = 1.1;
    ctx.beginPath();
    for (let i = 0; i < n; i++) {
      const depth = seeds.depth[i]!;
      const span = this.w + 500;
      const x = ((seeds.x[i]! + (this.t * speed * depth) / span) % 1 + 1) % 1 * span - 250;
      const y = seeds.y[i]! * this.h;
      const len = (90 + seeds.len[i]! * 150) * depth;
      ctx.moveTo(x, y);
      ctx.lineTo(x + len * dir, y - len * 0.06);
    }
    ctx.strokeStyle = `rgba(255,255,255,${0.05 + strength * 0.1 + shear * 0.04})`;
    ctx.stroke();
  }
}
